import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { conversationDayLabel, spokenDayLabel, startsNewDay } from "_shared/dates/compact-time-label";
import type AHDate from "_shared/dates/date";
import { useMinuteClock } from "_shared/dates/use-minute-clock";

const DayDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(1.5, 0),
  "&::before, &::after": { borderColor: theme.palette.divider },
}));

type ConversationDayDividerProps = {
  /** Any moment of the day the divider opens. Only its date is said. */
  date: AHDate;
};

/**
 * The line across a conversation where one day's messages end and the next day's begin: "Today",
 * "Yesterday", "Thu, Oct 15" (#7082). It says the day once, so the bubbles under it say only the time.
 * Drawn by every pane that lists messages — the proposal's Comments and each correspondence pane.
 */
export default function ConversationDayDivider(props: Readonly<ConversationDayDividerProps>) {
  const { date } = props;
  // "Today" becomes "Yesterday" at midnight without the pane being reopened.
  const now = useMinuteClock();
  return (
    <DayDivider role="separator" aria-label={spokenDayLabel(date)} data-testid="conversation-day-divider">
      <Typography aria-hidden variant="caption" color="text.secondary">
        {conversationDayLabel(date, now)}
      </Typography>
    </DayDivider>
  );
}

/**
 * Each item of a conversation, oldest first, paired with the day it opens — or `undefined` when it was
 * sent on the same day as the item above it. A host draws `ConversationDayDivider` above every item
 * that opens a day.
 */
export function withConversationDays<T>(items: readonly T[], dateOf: (item: T) => AHDate): { item: T; opensDay?: AHDate }[] {
  return items.map((item, index) => {
    const date = dateOf(item);
    const previous = index > 0 ? dateOf(items[index - 1]) : undefined;
    return { item, opensDay: startsNewDay(previous, date) ? date : undefined };
  });
}
